import React, { useEffect, useState } from 'react';
import type { FormRule } from 'antd';
import { Form, Select } from 'antd';
import debounce from 'lodash.debounce';
import { useGetClient } from 'api/clients/use-get-client.ts';
import { useGetManyClients } from 'api/clients/use-get-many-clients.ts';
import type { ClientRes } from 'api/swagger';
import type { ClientType } from 'types/client.ts';

interface IClientFormSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  rules?: FormRule[];
  disabled?: boolean;
  currentClientId?: number;
  clientTypes?: ClientType[];
  allowClear?: boolean;
  handleClientChange?: (option: { value: number; label: string }) => void;
  className?: string;
}

export const ClientFormSelect: React.FC<IClientFormSelectProps> = (props) => {
  const {
    name,
    handleClientChange,
    placeholder,
    label,
    rules,
    disabled = false,
    currentClientId,
    clientTypes,
    allowClear = false,
    className,
  } = props;

  const [clientSearch, setClientSearch] = useState('');
  const [items, setItems] = useState<ClientRes[]>([]);
  const { data: clients, isLoading: isLoadingClients } = useGetManyClients({
    limit: 10,
    search: clientSearch,
    clientTypes,
  });

  // To improve user experience, we display a short list of recommended clients (10 items).
  // However, the current client might not be included in the top 10 recommendations.
  // This code ensures the current client is retrieved and added to the items array to display it in transport form.
  const { data: currentClient, isLoading: isLoadingCurrentClient } = useGetClient(currentClientId);
  useEffect(() => {
    const data = clients?.data || [];
    const withCurrentClient =
      !clientSearch && data.length && currentClient && !data.some((item) => item.id === currentClient.id);

    if (withCurrentClient) {
      setItems([currentClient, ...data]);
    } else {
      setItems(data);
    }
  }, [currentClient, clients?.data, clientSearch]);

  const isLoading = isLoadingClients || (!!currentClientId && isLoadingCurrentClient);
  const handleClientSearch = debounce((value: string) => setClientSearch(value), 400);

  return (
    <Form.Item label={label} name={name} rules={rules} className={className}>
      <Select
        placeholder={placeholder}
        loading={isLoading}
        filterOption={false}
        notFoundContent={null}
        disabled={disabled}
        options={items.map((client) => ({
          value: client.id,
          label: client.shortName || client.name,
        }))}
        allowClear={allowClear}
        showSearch
        labelInValue
        onChange={handleClientChange}
        onSearch={handleClientSearch}
      />
    </Form.Item>
  );
};
